// src/oura/components/newscan/firstCheckFacts.ts
//
// What a First AI check draft says about the signing, in words (JOURNEY-SPEC
// v1.1 §2.5): the pitch line's date, who answered the form, how many things
// the form and the quote raised, the Start step's "From" and "For the plan"
// facts. Pure: the Business, Review and Start steps share one copy.

import { isFirstCheckDraft, type ScanDraft, type ScanDraftDecision } from "@service/aeo/scanDraftTypes";
import { plural } from "@oura/newscan/copy";
import { shortDate } from "./brandReview";

/** The cards the client's form and the quote raise (the "things to check"). */
export const FORM_CARD_TYPES: ReadonlyArray<ScanDraftDecision["type"]> = [
    "form_service_new",
    "form_service_missing",
    "form_outlet_new",
    "form_outlet_missing",
    "outlets_vs_plan",
    "outlets_exceed_slots",
];

/** The "For the plan" cards, shown under their own label after the question decisions. */
export const PLAN_CARD_TYPES: ReadonlyArray<ScanDraftDecision["type"]> = ["plan_keywords", "plan_outlet_names"];

export const isPlanCard = (d: ScanDraftDecision): boolean => PLAN_CARD_TYPES.includes(d.type);

/** A first_check draft's own cards: their other choices read as text buttons, the recommended one blue. */
export const isFirstCheckCard = (d: ScanDraftDecision): boolean => FORM_CARD_TYPES.includes(d.type) || isPlanCard(d);

/** "Sarah" for "Sarah Wong", "Aaron" for "Dr Aaron Lim". */
export function firstNameOf(name: string | null | undefined): string {
    const words = (name ?? "").trim().split(/\s+/).filter(Boolean);
    const rest = /^(dr\.?|datin|datuk|dato'?|mr|mrs|ms|puan|encik)$/i.test(words[0] ?? "") ? words.slice(1) : words;
    return rest[0] ?? "";
}

export function isFirstCheck(draft: ScanDraft | null | undefined): boolean {
    return Boolean(draft && isFirstCheckDraft(draft));
}

/** "3 Oct": the day the pitch scan ran. Null without a platform pitch. */
export function pitchDay(draft: ScanDraft): string | null {
    return shortDate(draft.firstCheck?.pitch?.scannedAt);
}

/** The pitch line (`presale_set`): the server's sentence, never listed with the notes. */
export function pitchLine(draft: ScanDraft): ScanDraftDecision | null {
    return draft.decisions.find((d) => d.type === "presale_set") ?? null;
}

/** Open cards the form and the quote raised. */
export function formThingsOpen(draft: ScanDraft): number {
    return formThingsOpenTitles(draft).length;
}

/** What each of those cards asks, in its own title ("Sarah names an outlet in Petaling Jaya"). */
export function formThingsOpenTitles(draft: ScanDraft): string[] {
    return draft.decisions.filter((d) => d.state === "open" && FORM_CARD_TYPES.includes(d.type)).map((d) => d.title);
}

/** Start step "From": "The pitch on 3 Oct, 47 kept", or "Drafted new". */
export function fromText(draft: ScanDraft): string {
    const day = pitchDay(draft);
    const changed = draft.changedVsPitch;
    if (!day || !draft.firstCheck?.pitch?.reusable || changed === null || changed === undefined) return "Drafted new";
    const total = draft.counts.total || draft.questions.length;
    return `The pitch on ${day}, ${Math.max(total - changed, 0)} kept`;
}

/** "Approved by Dana on 3 Oct (the pitch)". Null unless the pitch's approval carried. */
export function carriedText(draft: ScanDraft): string | null {
    const c = draft.carriedApproval;
    if (!c) return null;
    const day = shortDate(c.at);
    return `Approved by ${c.by}${day ? ` on ${day}` : ""} (the pitch)`;
}

/** Start step "For the plan": "8 priority keywords · 2 outlet names". Null when nothing goes to the plan. */
export function forPlanText(draft: ScanDraft): string | null {
    const fc = draft.firstCheck;
    if (!fc) return null;
    const keywords = fc.forPlan.priorityKeywords?.length ?? 0;
    const outlets = fc.forPlan.outletNames?.length ?? 0;
    const parts = [keywords > 0 && plural(keywords, "priority keyword"), outlets > 0 && plural(outlets, "outlet name")].filter(Boolean);
    return parts.length ? parts.join(" · ") : null;
}
